import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Grid,
  Toolbar,
  Stack,
  Box,
  Tooltip,
  DialogActions,
  Button,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch } from 'react-redux';
import { useUser } from '@/providers';

import { createCollection } from '../../../actions/collections';
import CollectionNameField from '@/components/collections/CollectionForm/CollectionNameField';
import CollectionProjectField from '@/components/collections/CollectionCopyModal/CollectionProjectField';
import CollectionCapacityField from '@/components/collections/CollectionForm/CollectionCapacityField';
import THIRD_PARTY_TYPES from '../../jobs/JobForm/thirdPartyTypes';
import toTitleCase from '../../../util/toTitleCase';

export default function CollectionCopyModal({
  entity_type,
  open,
  handleClose,
  collection,
}) {
  const dispatch = useDispatch();
  const is_volume = String(entity_type || '').toLowerCase() === 'volume';

  const [loading, setLoading] = useState(false);
  const [name, setName] = useState(collection ? `${collection.name} copy` : '');
  const [name_error, setNameError] = useState(false);
  const [capacity, setCapacity] = useState(collection?.capacity || '');
  const [capacity_error, setCapacityError] = useState(false);
  const user = useUser();
  const [project_uuid, setProjectUuid] = useState('');

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        name,
        source_type: THIRD_PARTY_TYPES.EVEFS.value,
        source_uri: collection.id,
        source_options: {
          type: entity_type,
          project_uuid: Boolean(project_uuid)
            ? user.selected_project
            : undefined,
        },
      };
      if (is_volume) {
        payload.capacity = capacity ? `${capacity}G` : undefined;
      }
      if (Boolean(project_uuid)) {
        payload.project_uuid = project_uuid;
      }
      if (user?.selected_project) {
        await dispatch(
          createCollection(user.selected_project, entity_type, payload),
        );
      }
      setLoading(false);
      handleClose();
    } catch (error) {
      setLoading(false);
    }
  };

  if (!collection) {
    return (
      <Dialog onClose={handleClose} open={open}>
        <DialogTitle></DialogTitle>
        <DialogContent></DialogContent>
      </Dialog>
    );
  }

  const not_enough_credits = Boolean(
    !user.cloudbender_enabled &&
      user.credits <= 0 &&
      !user.billing_account_id &&
      user.id === user.selected_project,
  );

  return (
    <Dialog onClose={handleClose} open={open}>
      <DialogTitle>
        <Grid
          container
          direction='row'
          justifyContent='space-between'
          alignItems='center'
        >
          <Grid size='grow'>
            Copy {toTitleCase(entity_type)} {collection.name}
          </Grid>
          <Grid size={1} sx={{ maxWidth: '25px' }}>
            <IconButton aria-label='close' onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent>
        <Box
          padding='10px'
          sx={{
            maxWidth: 400,
          }}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 12, lg: 12 }}>
              <CollectionNameField
                value={name}
                error={name_error}
                setValue={setName}
                setError={setNameError}
                setChanged={() => {}}
              />
            </Grid>
            {is_volume ? (
              <Grid size={{ xs: 12, md: 12, lg: 12 }}>
                <CollectionCapacityField
                  value={capacity}
                  error={capacity_error}
                  setValue={setCapacity}
                  setError={setCapacityError}
                  setChanged={() => {}}
                  minimum_capacity={parseInt(
                    (collection?.capacity || '').replace(/\D/g, ''),
                  )}
                />
              </Grid>
            ) : null}
            <Grid size={{ xs: 12, md: 12, lg: 12 }}>
              <CollectionProjectField
                value={project_uuid}
                setValue={setProjectUuid}
                setChanged={() => {}}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Toolbar>
          <Stack spacing={2} direction='row'>
            <Tooltip title={not_enough_credits ? 'Not Enough Credits' : ''}>
              <span>
                <Button
                  variant='contained'
                  color='brand'
                  onClick={handleSubmit}
                  disabled={
                    not_enough_credits ||
                    Boolean(name_error) ||
                    !Boolean(name) ||
                    (is_volume && (Boolean(capacity_error) || !Boolean(capacity))) ||
                    loading
                  }
                  startIcon={
                    loading ? <CircularProgress size={20} /> : undefined
                  }
                >
                  Submit
                </Button>
              </span>
            </Tooltip>

            <Button variant='outlined' onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
          </Stack>
        </Toolbar>
      </DialogActions>
    </Dialog>
  );
}
